import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * composer-v2/lib/linkDialog — open/close state for the link insert
 * popover. KAP-304 originally + post-user-smoke addition of a Text
 * field so the popover supports editing both the visible text and the
 * URL (Slack's "Add link" modal pattern).
 *
 * Keeping the state in React context (rather than as a Lexical command)
 * lets the popover render outside the editor tree while still being
 * driven by editor-side keyboard shortcuts and selection rects.
 *
 * `defaultText` is captured by the trigger (toolbar Link button or
 * Cmd+K shortcut) from the current selection's text content — if the
 * user has nothing selected, this is empty and the popover treats the
 * Text field as required (the user must fill in something to display).
 */

const LinkDialogContext = createContext(null);

export function LinkDialogProvider({ children }) {
  const [state, setState] = useState({
    isOpen: false,
    anchorRect: null,
    defaultUrl: '',
    defaultText: ''
  });

  const open = useCallback(({ anchorRect, defaultUrl, defaultText }) => {
    setState({
      isOpen: true,
      anchorRect: anchorRect || null,
      defaultUrl: defaultUrl || '',
      defaultText: defaultText || ''
    });
  }, []);

  const close = useCallback(() => {
    setState((prev) => (prev.isOpen
      ? { isOpen: false, anchorRect: null, defaultUrl: '', defaultText: '' }
      : prev));
  }, []);

  const value = useMemo(() => ({ ...state, open, close }), [state, open, close]);

  return <LinkDialogContext.Provider value={value}>{children}</LinkDialogContext.Provider>;
}

export function useLinkDialog() {
  const ctx = useContext(LinkDialogContext);
  if (!ctx) {
    throw new Error('useLinkDialog must be used inside <LinkDialogProvider>');
  }
  return ctx;
}
